import { useState } from "react";
import { ArrowRight, Check, Download, ShoppingBag } from "lucide-react";
import { ResourceDetailsModal } from "@/components/resources/ResourceDetailsModal";
import { LitPanel } from "@/components/ui/LitPanel";
import { buttonClass } from "@/components/ui/buttonStyles";
import type { ResourceItem } from "@/data/content";
import { useCart } from "@/hooks/useCart";
import { useHydrated } from "@/hooks/useHydrated";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useLocale } from "@/i18n/useLocale";
import { formatPrice } from "@/utils/price";

type Props = {
  resource: ResourceItem;
  /** Free resources: opens the email-gated download. */
  onAccessRequest: (resource: ResourceItem) => void;
  index?: number;
  headingLevel?: "h2" | "h3";
};

/**
 * One resource: cover with its badge, Playfair title, short description, and
 * the price beside the action. Free resources download after the email step;
 * paid ones go to the cart. The cover and title open the details dialog.
 */
export function ResourceCard({ resource: res, onAccessRequest, index = 0, headingLevel: Heading = "h3" }: Props) {
  const { t } = useAppTranslation("resourcesPage");
  const locale = useLocale();
  const cart = useCart();
  const hydrated = useHydrated();
  const Icon = res.icon;
  const paid = res.priceCents > 0;
  const price = paid ? formatPrice(res.priceCents, res.currency, locale) : t("free");
  const inCart = hydrated && paid && res.id ? cart.has(res.id) : false;
  const badge = res.badge?.trim() || res.type;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const priceLabel = paid ? price : <span className="text-teal-strong">{price}</span>;

  /* Leaving the details dialog first, so the cart drawer or email step isn't stacked under it. */
  function renderAction(closeDetails?: () => void) {
    if (paid && inCart) {
      return (
        <button
          type="button"
          onClick={() => {
            closeDetails?.();
            cart.open();
          }}
          aria-label={t("inCartAria", { title: res.title })}
          className={buttonClass("outline", "md", "!min-h-10 !px-4")}
        >
          <Check className="size-4 text-teal-strong" aria-hidden="true" />
          <span>{t("inCart")}</span>
        </button>
      );
    } else if (paid) {
      return (
        <button
          type="button"
          onClick={() => {
            closeDetails?.();
            cart.add(res);
          }}
          aria-label={t("addToCartAria", { title: res.title, price })}
          className={buttonClass("primary", "md", "!min-h-10 !px-4")}
        >
          <ShoppingBag className="size-4" aria-hidden="true" />
          <span>{t("addToCart")}</span>
        </button>
      );
    } else {
      const isDownload = Boolean(res.downloadAs);
      return (
        <button
          type="button"
          onClick={() => {
            closeDetails?.();
            onAccessRequest(res);
          }}
          aria-label={
            isDownload
              ? t("downloadAria", { title: res.title, type: res.type })
              : t("openAria", { title: res.title, type: res.type })
          }
          className={buttonClass("primary", "md", "!min-h-10 !px-4")}
        >
          {isDownload ? <Download className="size-4" aria-hidden="true" /> : null}
          <span>{res.actionLabel?.trim() || t("downloadDefault")}</span>
          {isDownload ? null : <ArrowRight className="size-4" aria-hidden="true" />}
        </button>
      );
    }
  }

  return (
    <LitPanel as="li" className="flex flex-col">
      <div
        data-reveal=""
        style={{ ["--reveal-delay" as string]: `${(index % 3) * 80}ms` }}
        className="flex h-full flex-col"
      >
        <button
          type="button"
          onClick={() => setDetailsOpen(true)}
          aria-label={t("viewDetailsAria", { title: res.title })}
          className="relative block aspect-[3/2] w-full cursor-zoom-in overflow-hidden bg-teal-soft text-left"
        >
          <span
            className={`absolute left-4 top-4 z-10 rounded-full px-3 py-1 text-xs font-semibold shadow-lit ${
              res.badge?.trim() ? "bg-[#9e1a1a] text-white" : "bg-white text-teal-strong"
            }`}
          >
            {badge}
          </span>
          {res.coverSrc ? (
            <img
              src={res.coverSrc}
              alt={t("coverAlt", { title: res.title })}
              width={600}
              height={400}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-[var(--ease-out-expo)] group-hover/panel:scale-[1.03]"
            />
          ) : (
            <div className="light-pool grid h-full place-items-center [--pool-x:50%] [--pool-y:40%]" aria-hidden="true">
              <Icon className="size-14 text-teal-strong" strokeWidth={1.25} />
            </div>
          )}
        </button>
        <div className="flex flex-1 flex-col p-6 sm:p-7">
          <Heading className="font-display text-xl font-semibold leading-snug text-brand-primary">
            <button
              type="button"
              onClick={() => setDetailsOpen(true)}
              className="text-left underline-offset-4 hover:underline"
            >
              {res.title}
            </button>
          </Heading>
          <p className="mt-3 line-clamp-3 whitespace-pre-line text-base leading-normal text-ink-soft">
            {res.desc}
          </p>
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            aria-label={t("viewDetailsAria", { title: res.title })}
            className="mt-3 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-teal-strong underline-offset-4 hover:underline"
          >
            {t("viewDetails")}
            <ArrowRight className="size-4" aria-hidden="true" />
          </button>
          <div className="mt-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-6">
            <p className="font-display text-3xl font-semibold leading-none text-brand-primary">
              {priceLabel}
            </p>
            {renderAction()}
          </div>
        </div>
      </div>
      {detailsOpen ? (
        <ResourceDetailsModal
          resource={res}
          price={priceLabel}
          action={renderAction(() => setDetailsOpen(false))}
          onDismiss={() => setDetailsOpen(false)}
        />
      ) : null}
    </LitPanel>
  );
}
